import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    this.removeInjectedDuplicatePrices();
    requestAnimationFrame(this.removeInjectedDuplicatePrices);
    setTimeout(this.removeInjectedDuplicatePrices, 250);
    setTimeout(this.removeInjectedDuplicatePrices, 1000);
    setTimeout(this.removeInjectedDuplicatePrices, 2000);

    const priceWrapper = this.closest('.av-price-sold-line__price');
    if (priceWrapper) {
      this.injectedPriceObserver = new MutationObserver(this.removeInjectedDuplicatePrices);
      this.injectedPriceObserver.observe(priceWrapper, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.injectedPriceObserver?.disconnect();
  }

  /**
   * Some bundle apps inject their own price next to the theme price.
   * Keep the theme's compare-at + sale markup and hide only those injected duplicates.
   */
  removeInjectedDuplicatePrices = () => {
    const priceWrapper = this.closest('.av-price-sold-line__price');
    if (!priceWrapper) return;

    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const allowedSelectors = '.price__regular, .price__sale, .unit-price, .volume-pricing-note';
    const looksLikePrice = (text) => /(?:€|\$|£)\s*\d|\d[\d.,]*\s*(?:€|\$|£)/.test(text || '');
    const hideNode = (node) => {
      if (node instanceof Text) {
        if (looksLikePrice(node.textContent)) node.textContent = '';
        return;
      }

      if (!(node instanceof HTMLElement)) return;
      if (node.matches(allowedSelectors) || node.closest(allowedSelectors)) return;
      if (!looksLikePrice(node.textContent)) return;

      node.hidden = true;
      node.style.display = 'none';
      node.dataset.avHiddenDuplicatePrice = 'true';
    };

    Array.from(this.childNodes).forEach((node) => {
      if (node === priceContainer || (node instanceof HTMLElement && node.matches('[ref="volumePricingNote"]'))) return;
      hideNode(node);
    });

    Array.from(priceContainer.childNodes).forEach((node) => {
      if (node instanceof HTMLElement && node.matches(allowedSelectors)) return;
      hideNode(node);
    });

    priceContainer.querySelectorAll('.price__sale').forEach((salePrice) => {
      salePrice.querySelectorAll('.pmpr-theme-current-price').forEach((pumperCurrentPrice) => {
        hideNode(pumperCurrentPrice);
      });

      salePrice.querySelectorAll('.pmpr-theme-compare-price').forEach((pumperComparePrice) => {
        const compareText = pumperComparePrice.textContent?.trim() || '';
        const leadingEuroMatch = compareText.match(/^€\s*(.+?)\s*€?$/);
        if (leadingEuroMatch) {
          pumperComparePrice.textContent = `${leadingEuroMatch[1]}€`;
          return;
        }

        const regularPriceText = pumperComparePrice.textContent?.replace(/\s+/g, '') || '';
        if (regularPriceText.endsWith('€') || !regularPriceText.includes('€')) return;

        pumperComparePrice.textContent = `${regularPriceText.replace('€', '')}€`;
      });

      Array.from(salePrice.childNodes).forEach((node) => {
        if (node instanceof HTMLElement && node.matches('.price-item__group')) return;
        hideNode(node);
      });

      const priceGroups = Array.from(salePrice.querySelectorAll(':scope > .price-item__group'));
      priceGroups.slice(0, Math.max(0, priceGroups.length - 2)).forEach((priceGroup) => hideNode(priceGroup));
    });
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    requestAnimationFrame(this.removeInjectedDuplicatePrices);

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
